// Backend Integration utilities for protected data access
// Bu dosya gerçek backend ile entegrasyon için gerekli fonksiyonları içerir

export interface ViewPermissionRequest {
  password: string;
  dataType: string;
  dataValue?: string;
  userId?: string;
  timestamp?: string;
  pageContext?: string;
}

export interface ViewPermissionResponse {
  success: boolean;
  message?: string;
  sessionId?: string;
}

export interface AccessLog {
  id: string;
  adminId: string;
  adminEmail: string;
  dataType: string;
  dataValue: string;
  accessedAt: string;
  ipAddress?: string;
  userAgent?: string;
  sessionId?: string;
  pageContext?: string;
}

// Real API Calls for sensitive data access verification  
export const verifyViewPermission = async (request: ViewPermissionRequest): Promise<ViewPermissionResponse> => {
  const token = localStorage.getItem('adminToken');
  if (!token) {
    throw new Error('Admin token bulunamadı');
  }

  try {
    
    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'https://barkleyclub-v2-production.up.railway.app'}/api/super-admin/verify-view-permission`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-API-Key': process.env.REACT_APP_API_KEY || (() => {
          console.error('REACT_APP_API_KEY environment variable is not set!');
          throw new Error('API Key is required. Please set REACT_APP_API_KEY in your .env file.');
        })()
      },
      body: JSON.stringify({
        password: request.password,
        dataType: request.dataType,
        dataValue: request.dataValue,
        dogId: request.userId, // Mapping dogId/userId
        pageContext: request.pageContext || 'admin-panel'
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        message: errorData.message || 'Şifre doğrulaması başarısız'
      };
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw new Error('Şifre doğrulaması sırasında hata oluştu');
  }
};

export const logDataAccess = async (logData: Omit<AccessLog, 'id' | 'accessedAt'>): Promise<boolean> => {
  const token = localStorage.getItem('adminToken');
  if (!token) {
    return false;
  }

  try {
    
    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'https://barkleyclub-v2-production.up.railway.app'}/api/super-admin/access-logs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-API-Key': process.env.REACT_APP_API_KEY || (() => {
          console.error('REACT_APP_API_KEY environment variable is not set!');
          throw new Error('API Key is required. Please set REACT_APP_API_KEY in your .env file.');
        })()
      },
      body: JSON.stringify({
        dataType: logData.dataType,
        dataValue: logData.dataValue,
        sessionId: logData.sessionId,
        pageContext: logData.pageContext || 'admin-panel',
        userAgent: navigator.userAgent
      })
    });

    if (!response.ok) {
      return false;
    }

    const data = await response.json();
    return data.success;
  } catch (error) {
    return false;
  }
};

// Mock function for development/testing
export const mockVerifyViewPermission = async (password: string): Promise<ViewPermissionResponse> => {
  // Mock: Password must be "admin123" for testing
  const validPassword = "admin123";
  
  if (password === validPassword) {
    return {
      success: true,
      message: 'Şifre doğrulaması başarılı',
      sessionId: `session_${Date.now()}`
    };
  }
  
  return {
    success: false,
    message: 'Geçersiz şifre'
  };
};

// Get admin info from token or localStorage
export const getCurrentAdminInfo = () => {
  try {
    const adminData = localStorage.getItem('adminData');
    if (adminData) {
      return JSON.parse(adminData);
    }
    
    // Fallback: extract from token (basic)
    const token = localStorage.getItem('adminToken');
    if (token) {
      return {
        id: 'current_admin',
        email: 'admin@barkleyclub.com',
        name: 'Admin User'
      };
    }
    
    return null;
  } catch (error) {
    return null;
  }
};
